/* eslint-disable @typescript-eslint/no-misused-promises */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useTheme, Box, Typography } from '@mui/material';
import { type GridColumns, type GridValidRowModel } from '@mui/x-data-grid';
import { CustomDataGrid } from 'components';
import { colorTheme } from 'theme';
import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { strings } from 'i18n/Strings';
import { commonConstantTexts } from 'constants/constantText';
import { checkSortingValues, dateFormat } from 'utils/GeneralUtil';
import { CurrencyFormater } from 'components/helpers/commonFunctions/CurrencyFormater';
import { getBankFileRecordsByStatus } from 'service/UploadService';
import {
  setCurrentPage,
  setSortBy,
  setSortOrder,
} from 'store/component/pagination';

export interface BankFileRecordType {
  transactionDate: string;
  transactionReference: string;
  transactionNarrative: string;
  creditorName: string;
  amount: string;
  status: string;
}

type GridRowData = Record<string, unknown>;

const NonReconciledTransactionsGrid = (): JSX.Element => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const colors = colorTheme(theme.palette.mode);
  const [bankFileRecordsList, setBankFileRecordsList] = useState<
    BankFileRecordType[] | unknown
  >([]);
  const [pageCount, setPageCount] = useState(null);
  const [searchValue, setSearchValue] = useState('');

  const pageInfo = useSelector(
    (state) => state?.pagination?.uploadDetailsNonReconciledStatus,
  );

  const handleSearch = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    setSearchValue(event.target.value);
  };

  const getAllBankRecordFileByStatus = async (): Promise<void> => {
    try {
      const data = await getBankFileRecordsByStatus(
        commonConstantTexts.nonReconciled,
        Number(pageInfo?.page) + 1,
        pageInfo?.pageSize,
        pageInfo?.sortBy,
        pageInfo?.sortOrder,
        searchValue,
      );
      setBankFileRecordsList(data?.rows);
      setPageCount(data?.count);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    void getAllBankRecordFileByStatus();
  }, [
    pageInfo?.page,
    pageInfo?.pageSize,
    searchValue,
    pageInfo?.sortBy,
    pageInfo?.sortOrder,
  ]);

  const columns: GridColumns<GridValidRowModel> = [
    {
      field: 'contributionBankFile.contributionFileId',
      width: 150,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.bankFileId}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.contributionBankFile.contributionFileId}
        </Typography>
      ),
    },
    {
      field: 'date',
      width: 150,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.transactionDate}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {dateFormat(row?.date)}
        </Typography>
      ),
    },
    {
      field: 'transactionReference',
      width: 200,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.transactionReference}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.transactionReference || '-'}
        </Typography>
      ),
    },
    {
      field: 'transactionNarrative',
      width: 200,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.transactionNarrative}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.transactionNarrative || '-'}
        </Typography>
      ),
    },
    {
      field: 'debtorName',
      width: 200,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.debtorName}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.debtorName}
        </Typography>
      ),
    },
    {
      field: 'creditorName',
      width: 200,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.creditorName}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.creditorName}
        </Typography>
      ),
    },
    {
      field: 'creditOrDebit',
      sortable: false,
      width: 150,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.creditOrDebit}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.fundFlowType}
        </Typography>
      ),
    },
    {
      field: 'amount',
      width: 200,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.amountLabel}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {CurrencyFormater(Number(Math.abs(row?.amount)).toFixed(2)) || '-'}
        </Typography>
      ),
    },
    {
      field: 'status',
      width: 200,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.status}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.status}
        </Typography>
      ),
    },
  ];

  return (
    <Box>
      <CustomDataGrid
        columns={columns}
        rows={bankFileRecordsList as GridRowData[]}
        getRowId={(row: GridRowData) =>
          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
          `${String(row.id)}`
        }
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0
            ? commonConstantTexts.evenText
            : commonConstantTexts.oddText
        }
        currentPage="uploadDetailsNonReconciledStatus"
        pageCount={pageCount}
        searchEnabled={true}
        handleSearch={handleSearch}
        searchPlaceholder={strings.displayText.searchLabel8}
        sortModel={checkSortingValues(pageInfo)}
        sortingMode={strings.displayText.sortingServer}
        onSortModelChange={(mode: any[][]) => {
          dispatch(
            setSortBy({
              key: 'uploadDetailsNonReconciledStatus',
              value: mode[0].field,
            }),
          );
          dispatch(
            setSortOrder({
              key: 'uploadDetailsNonReconciledStatus',
              value: mode[0].sort,
            }),
          );
          dispatch(
            setCurrentPage({
              key: 'uploadDetailsNonReconciledStatus',
              value: 0,
            }),
          );
        }}
      />
    </Box>
  );
};

export default NonReconciledTransactionsGrid;
